/**
 * Form parts shared by the onboarding form (form.ts) and the offboarding form (offboarding.ts): the labelled field
 * wrapper, radios, switches, and the directory people picker (GET /api/directory/users, two characters minimum,
 * Graph is reached only by the API). Each form keeps its own state and hands these a FormCtx: the App, its current
 * field errors, and how to clear them when a field is touched.
 */
import { html, nothing, type TemplateResult } from 'lit-html';
import { ApiError, searchDirectory, type DirectoryUser, type PickerRow } from './api';
import type { App } from './app';
import type { FieldError } from '../../api/src/requests/validation';

export interface FormCtx {
  app: App;
  errors(): FieldError[];
  touch(...fields: string[]): void;
}

export const val = (e: Event) => (e.target as HTMLInputElement).value;
export const checked = (e: Event) => (e.target as HTMLInputElement).checked;
export const errorsFor = (ctx: FormCtx, field: string) => ctx.errors().filter((x) => x.field === field).map((x) => x.message);
export const invalid = (ctx: FormCtx, f: string) => (errorsFor(ctx, f).length ? 'true' : 'false');

export function field(
  ctx: FormCtx,
  id: string,
  label: string,
  control: TemplateResult,
  opts: { required?: boolean; hint?: string; errField?: string; wide?: boolean; soft?: string } = {},
): TemplateResult {
  const errs = errorsFor(ctx, opts.errField ?? id);
  return html`
    <div class="field ${errs.length ? 'has-err' : ''} ${opts.wide ? 'wide' : ''}">
      <label for=${id}>${label}${opts.required ? html`<span class="req" aria-hidden="true"> *</span>` : nothing}</label>
      ${control}
      ${opts.hint ? html`<p class="hint" id="${id}-hint">${opts.hint}</p>` : nothing}
      ${opts.soft ? html`<p class="soft" role="status">${opts.soft}</p>` : nothing}
      ${errs.map((m) => html`<p class="ferr" id="${id}-err">${m}</p>`)}
    </div>`;
}

export function radioGroup(
  ctx: FormCtx,
  id: string,
  label: string,
  options: readonly string[],
  value: string,
  set: (v: string) => void,
): TemplateResult {
  const errs = errorsFor(ctx, id);
  return html`
    <fieldset class="field radios ${errs.length ? 'has-err' : ''}">
      <legend>${label}<span class="req" aria-hidden="true"> *</span></legend>
      <div class="opts">
        ${options.map(
          (o) => html`<label class="opt"><input type="radio" name=${id} .value=${o} .checked=${value === o}
            @change=${() => {
              set(o);
              ctx.touch(id);
              ctx.app.redraw();
            }}> ${o}</label>`,
        )}
      </div>
      ${errs.map((m) => html`<p class="ferr">${m}</p>`)}
    </fieldset>`;
}

export function switchRow(ctx: FormCtx, label: string, on: boolean, set: (v: boolean) => void, hint?: string): TemplateResult {
  return html`
    <label class="switch">
      <input type="checkbox" role="switch" .checked=${on} @change=${(e: Event) => {
        set(checked(e));
        ctx.app.redraw();
      }}>
      <span class="track" aria-hidden="true"></span>
      <span class="sw-label">${label}${hint ? html`<small>${hint}</small>` : nothing}</span>
    </label>`;
}

// ---- people picker ----------------------------------------------------------------------------------------------

export interface Person {
  displayName: string;
  email: string;
  /** Entra object id, when the person came from a directory search (used for the devices lookup). */
  id?: string;
  department?: string | null;
}

export interface PeoplePicker {
  sel: Person | null;
  /** The search is open although someone is selected ("Change"); dismissing it keeps `sel` (2c fix: the manager's
   *  "defaults to you" survives an abandoned search, as the canvas did). */
  editing: boolean;
  q: string;
  results: DirectoryUser[];
  loading: boolean;
  error: string;
  seq: number;
  timer?: number;
}

export const newPeople = (sel: Person | null = null): PeoplePicker => ({ sel, editing: false, q: '', results: [], loading: false, error: '', seq: 0 });

export function searchPeople(app: App, pp: PeoplePicker, q: string): void {
  pp.q = q;
  window.clearTimeout(pp.timer);
  if (q.trim().length < 2) {
    pp.results = [];
    pp.loading = false;
    pp.error = '';
    app.redraw();
    return;
  }
  pp.loading = true;
  app.redraw();
  pp.timer = window.setTimeout(async () => {
    const seq = ++pp.seq;
    try {
      const rows = await searchDirectory(q.trim());
      if (seq !== pp.seq) return;
      pp.results = rows;
      pp.error = '';
    } catch (e) {
      if (seq !== pp.seq) return;
      pp.results = [];
      pp.error = e instanceof ApiError ? e.message : 'The directory search is not available right now';
    }
    pp.loading = false;
    app.redraw();
  }, 300);
}

/** Close an open search without changing the selection. */
function dismiss(app: App, pp: PeoplePicker, id: string): void {
  window.clearTimeout(pp.timer);
  pp.seq++;
  pp.editing = false;
  pp.q = '';
  pp.results = [];
  pp.loading = false;
  pp.error = '';
  app.redraw();
  requestAnimationFrame(() => document.getElementById(id)?.focus());
}

export function peoplePicker(
  ctx: FormCtx,
  id: string,
  label: string,
  pp: PeoplePicker,
  errField: string,
  required: boolean,
  hint: string,
  onPick?: (p: Person, u: DirectoryUser) => void,
): TemplateResult {
  const { app } = ctx;
  const control =
    pp.sel && !pp.editing
      ? html`<div class="person">
          <span class="avatar sm" aria-hidden="true">${(pp.sel.displayName[0] ?? '?').toUpperCase()}</span>
          <span class="who"><b>${pp.sel.displayName}</b>${pp.sel.displayName !== pp.sel.email ? html`<span>${pp.sel.email}</span>` : nothing}</span>
          <button type="button" class="mini" id=${id} @click=${() => {
            pp.editing = true; // keep `sel`: an abandoned search changes nothing
            app.redraw();
            requestAnimationFrame(() => document.getElementById(id)?.focus());
          }}>Change</button>
        </div>`
      : html`<div class="combo">
            <input id=${id} type="search" placeholder="Type at least 2 letters of a name or e-mail" autocomplete="off"
              .value=${pp.q} aria-invalid=${invalid(ctx, errField)} @input=${(e: Event) => searchPeople(app, pp, val(e))}
              @keydown=${(e: KeyboardEvent) => {
                if (e.key === 'Escape' && pp.sel) dismiss(app, pp, id);
              }}>
            ${pp.sel
              ? html`<button type="button" class="combo-toggle" @click=${() => dismiss(app, pp, id)}>Keep ${pp.sel.displayName}</button>`
              : nothing}
          </div>
          ${pp.q.trim().length >= 2
            ? html`<ul class="results" role="listbox" aria-label="${label} results">
                ${pp.loading ? html`<li class="none">Searching…</li>` : nothing}
                ${!pp.loading && pp.error ? html`<li class="none err">${pp.error}</li>` : nothing}
                ${!pp.loading && !pp.error && !pp.results.length ? html`<li class="none">No matching people</li>` : nothing}
                ${pp.results.map(
                  (u) => html`<li><button type="button" role="option" aria-selected="false" @click=${() => {
                    const p: Person = { displayName: u.displayName, email: u.email, id: u.id, department: u.department };
                    pp.sel = p;
                    pp.editing = false;
                    pp.q = '';
                    pp.results = [];
                    ctx.touch(errField);
                    onPick?.(p, u);
                    app.redraw();
                  }}><b>${u.displayName}</b><span>${u.email}${u.jobTitle ? ` · ${u.jobTitle}` : ''}</span></button></li>`,
                )}
              </ul>`
            : nothing}`;
  return field(ctx, id, label, control, { required, hint, errField });
}

// ---- select (Phase 2d: the department / category / role selects stop being inline) ------------------------------

export function select(
  ctx: FormCtx,
  id: string,
  label: string,
  options: readonly string[],
  value: string,
  set: (v: string) => void,
  opts: { required?: boolean; hint?: string; placeholder?: string; errField?: string; disabled?: boolean } = {},
): TemplateResult {
  const errField = opts.errField ?? id;
  return field(
    ctx,
    id,
    label,
    html`<select id=${id} aria-invalid=${invalid(ctx, errField)} ?disabled=${opts.disabled ?? false} @change=${(e: Event) => {
      set(val(e));
      ctx.touch(errField);
      ctx.app.redraw();
    }}>
      <option value="" ?selected=${!value}>${opts.placeholder ?? `Choose ${label.toLowerCase()}`}</option>
      ${options.map((o) => html`<option value=${o} ?selected=${value === o}>${o}</option>`)}
    </select>`,
    { required: opts.required, hint: opts.hint, errField },
  );
}

// ---- multi-select picker over a picker table (Phase 2d; the admin template editor's sites and call queues) --------
// (form.ts keeps its own copy bound to the onboarding form's state; this one takes its state as an argument.)

export interface MultiPick {
  sel: string[];
  q: string;
  open: boolean;
}
export const newMultiPick = (sel: string[] = []): MultiPick => ({ sel, q: '', open: false });

export function multiPicker(
  ctx: FormCtx,
  id: string,
  label: string,
  rows: PickerRow[],
  st: MultiPick,
  opts: { placeholder: string; errField: string; match(r: PickerRow, q: string): boolean; detail(r: PickerRow): string },
): TemplateResult {
  const { app } = ctx;
  const q = st.q.trim().toLowerCase();
  const open = st.open || q !== '';
  const matches = open ? rows.filter((r) => !st.sel.includes(r.id) && (!q || opts.match(r, q))) : [];
  const close = () => {
    st.open = false;
    st.q = '';
    app.redraw();
  };
  const control = html`
    ${st.sel.length
      ? html`<ul class="sel-chips">
          ${st.sel.map((cid) => {
            const name = rows.find((x) => x.id === cid)?.label ?? cid;
            return html`<li class="sel-chip">${name}<button type="button" aria-label="Remove ${name}" @click=${() => {
              st.sel = st.sel.filter((x) => x !== cid);
              ctx.touch(opts.errField);
              app.redraw();
            }}>×</button></li>`;
          })}
        </ul>`
      : nothing}
    <div class="combo">
      <input id=${id} type="search" placeholder=${opts.placeholder} autocomplete="off" .value=${st.q}
        aria-expanded=${open ? 'true' : 'false'} aria-invalid=${invalid(ctx, opts.errField)}
        @focus=${() => {
          if (!st.open) {
            st.open = true;
            app.redraw();
          }
        }}
        @input=${(e: Event) => {
          st.q = val(e);
          st.open = true;
          app.redraw();
        }}
        @keydown=${(e: KeyboardEvent) => e.key === 'Escape' && close()}>
      <button type="button" class="combo-toggle" aria-label=${open ? `Close ${label} list` : `Show all ${label}`}
        @click=${() => {
          if (open) close();
          else {
            st.open = true;
            app.redraw();
          }
        }}>${open ? 'Done' : 'Browse'}</button>
    </div>
    ${open
      ? html`<ul class="results" role="listbox" aria-label=${label}>
          ${matches.length ? nothing : html`<li class="none">${rows.length ? 'No matches' : 'The list is empty'}</li>`}
          ${matches.slice(0, 100).map(
            (r) => html`<li><button type="button" role="option" aria-selected="false" @click=${() => {
              st.sel = [...st.sel, r.id];
              st.q = '';
              ctx.touch(opts.errField);
              app.redraw();
              requestAnimationFrame(() => document.getElementById(id)?.focus());
            }}><b>${r.label}</b>${opts.detail(r) ? html`<span>${opts.detail(r)}</span>` : nothing}</button></li>`,
          )}
        </ul>`
      : nothing}`;
  return field(ctx, id, label, control, { errField: opts.errField, wide: true });
}
